/**
 * SharePoint Authentication Handler
 * Handles authentication for SharePoint/Office Online environments using double-jump technique
 */

// We'll import showSharePointAuthDialog dynamically to avoid circular imports

let isInitialized = false;
let authenticationState = {
  isAuthenticated: false,
  user: null,
  lastCheck: null
};

/**
 * Initialize SharePoint authentication environment
 */
export const initializeSharePoint = () => {
  if (isInitialized) {
    return;
  }
  
  console.log("[SHAREPOINT-AUTH] Initializing SharePoint environment...");
  
  // Set up any SharePoint-specific initialization
  setupTokenExpiryMonitoring();
  
  isInitialized = true;
  console.log("[SHAREPOINT-AUTH] ✅ SharePoint environment initialized");
};

/**
 * Check if SharePoint environment is ready for authentication
 * @returns {boolean} - Whether SharePoint auth is ready
 */
export const isSharePointReady = () => {
  // Check if we have Office context and necessary APIs available
  return typeof Office !== 'undefined' && 
         Office.context && 
         Office.context.ui &&
         typeof Office.context.ui.displayDialogAsync === 'function';
};

/**
 * Handle SharePoint authentication flow
 * @returns {Promise<Object>} - Authentication result
 */
export const handleSharePointAuth = async () => {
  console.log("[SHAREPOINT-AUTH] Starting SharePoint authentication flow...");
  
  try {
    // Check for existing authentication
    const existingAuth = await checkSharePointAuthStatus();
    if (existingAuth.isAuthenticated) {
      console.log("[SHAREPOINT-AUTH] ✅ User already authenticated");
      return {
        success: true,
        component: 'PolicyReference',
        message: 'SharePoint authentication successful',
        authType: 'sharepoint'
      };
    }
    
    console.log("[SHAREPOINT-AUTH] No existing authentication found - showing Login component");
    return {
      success: false,
      component: 'Login',
      message: 'SharePoint authentication required',
      authType: 'sharepoint'
    };
    
  } catch (error) {
    console.error("[SHAREPOINT-AUTH] SharePoint authentication error:", error);
    return {
      success: false,
      component: 'AuthenticationError',
      message: 'SharePoint authentication failed: ' + error.message,
      authType: 'sharepoint'
    };
  }
};

/**
 * Check current SharePoint authentication status
 * @returns {Promise<Object>} - Authentication status
 */
const checkSharePointAuthStatus = async () => {
  console.log("[SHAREPOINT-AUTH] 🔍 REFRESH TOKEN STATUS CHECK:");
  
  try {
    const isUserLoggedIn = localStorage.getItem("IsUserLoggedIn") === "true";
    const userData = localStorage.getItem("UserData");
    
    if (!isUserLoggedIn || !userData) {
      console.log("[SHAREPOINT-AUTH] ❌ No user data found in localStorage");
      console.log("[SHAREPOINT-AUTH] - IsUserLoggedIn:", isUserLoggedIn);
      console.log("[SHAREPOINT-AUTH] - UserData:", userData ? "Present" : "Missing");
      return { isAuthenticated: false, user: null };
    }
    
    let parsedUserData;
    try {
      parsedUserData = JSON.parse(userData);
      console.log("[SHAREPOINT-AUTH] ✅ User data parsed successfully");
      console.log("[SHAREPOINT-AUTH] - User email:", parsedUserData.profile?.email);
      console.log("[SHAREPOINT-AUTH] - Access token:", parsedUserData.access_token ? "Present" : "Missing");
    } catch (parseError) {
      console.error("[SHAREPOINT-AUTH] ❌ Failed to parse user data:", parseError);
      return { isAuthenticated: false, user: null };
    }
    
    // You could add token expiration validation here
    console.log("[SHAREPOINT-AUTH] ✅ User appears to be authenticated");
    
    authenticationState = {
      isAuthenticated: true,
      user: parsedUserData,
      lastCheck: new Date()
    };
    
    return authenticationState;
    
  } catch (error) {
    console.error("[SHAREPOINT-AUTH] Error checking auth status:", error);
    return { isAuthenticated: false, user: null };
  }
};

/**
 * Set up token expiry monitoring for SharePoint
 */
const setupTokenExpiryMonitoring = () => {
  console.log("[SHAREPOINT-AUTH] 🕐 Starting token expiry monitor...");
  
  // Check token status every 5 minutes
  const monitorInterval = setInterval(() => {
    checkSharePointAuthStatus().then(status => {
      if (!status.isAuthenticated) {
        console.log("[SHAREPOINT-AUTH] Token expired - user needs to re-authenticate");
        localStorage.setItem("IsUserLoggedIn", "false");
        localStorage.removeItem("UserData");
        console.log("[SHAREPOINT-AUTH] 🗑️ Expired user data removed from localStorage");
        clearInterval(monitorInterval);
      }
    }).catch(error => {
      console.error("[SHAREPOINT-AUTH] Token monitoring error:", error);
    });
  }, 5 * 60 * 1000); // 5 minutes
  
  console.log("[SHAREPOINT-AUTH] ✅ Token expiry monitor started (checking every 5 minutes)");
};

/**
 * Show SharePoint authentication dialog using double-jump technique
 * @param {string} authUrl - Authentication URL 
 * @param {string} action - "signin" or "signout"
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const showSharePointAuthDialog = (authUrl, action, onSuccess, onError) => {
  console.log("[SHAREPOINT-DIALOG] Starting SharePoint authentication dialog...");
  console.log("[SHAREPOINT-DIALOG] - Auth URL:", authUrl);
  console.log("[SHAREPOINT-DIALOG] - Action:", action);
  
  // For SharePoint, use the double jump approach
  // First dialog will be our intermediary that never redirects
  const doubleJumpUrl = `${window.location.origin}/login/double-jump.html?originalUrl=${encodeURIComponent(authUrl)}&action=${action}`;
  
  console.log("[SHAREPOINT-DIALOG] Double jump URL:", doubleJumpUrl);
  
  let dialog;

  Office.context.ui.displayDialogAsync(
    doubleJumpUrl,
    { height: 60, width: 40, displayInIframe: false },
    (asyncResult) => {
      if (asyncResult.status === "failed") {
        let errorDescription = "";

        switch (asyncResult.error.code) {
          case 12004:
            errorDescription = "Domain is not trusted";
            break;
          case 12005:
            errorDescription = "HTTPS is required";
            break;
          case 12007:
            errorDescription = "A dialog is already opened.";
            break;
          default:
            errorDescription = asyncResult.error.message;
            break;
        }

        console.error("[SHAREPOINT-DIALOG] Double jump dialog failed:", errorDescription);
        if (onError) onError(errorDescription);
        return;
      }

      dialog = asyncResult.value;
      console.log("[SHAREPOINT-DIALOG] ✅ Double jump dialog opened successfully");

      // Handle messages from the double jump dialog
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (arg) => {
          try {
            console.log("[SHAREPOINT-DIALOG] 📨 Received message:", arg.message);
            console.log("[SHAREPOINT-DIALOG] 📨 Message type:", typeof arg.message);
            console.log("[SHAREPOINT-DIALOG] 📨 Message length:", arg.message.length);
            
            if (arg.message === "SignOutSuccess") {
              console.log("[SHAREPOINT-DIALOG] ✅ Sign out successful");
              setTimeout(() => {
                localStorage.setItem("IsUserLoggedIn", "false");
                localStorage.removeItem("UserData");
                console.log("[SHAREPOINT-DIALOG] 🗑️ User data removed from localStorage");
                if (onSuccess) onSuccess(null);
                dialog.close();
              }, 1000);
            } else if (arg.message.startsWith("ERROR:")) {
              console.error("[SHAREPOINT-DIALOG] ❌ Authentication error:", arg.message);
              if (onError) onError(arg.message.substring(6));
              dialog.close();
            } else {
              console.log("[SHAREPOINT-DIALOG] ✅ Authentication successful - processing user data");
              
              // Debug the message parsing
              console.log("[SHAREPOINT-DIALOG] 🔍 Attempting to parse message...");
              let msg;
              try {
                msg = JSON.parse(arg.message);
                console.log("[SHAREPOINT-DIALOG] ✅ Message parsed successfully");
                console.log("[SHAREPOINT-DIALOG] 📋 Parsed message structure:", {
                  hasResponse: !!msg.response,
                  hasCode: !!msg.code,
                  responseType: typeof msg.response,
                  responseKeys: msg.response ? Object.keys(msg.response) : []
                });
              } catch (parseError) {
                console.error("[SHAREPOINT-DIALOG] ❌ JSON parse error:", parseError);
                console.error("[SHAREPOINT-DIALOG] ❌ Raw message:", arg.message);
                throw parseError;
              }
              
              // Store in localStorage
              console.log("[SHAREPOINT-DIALOG] 💾 Setting localStorage...");
              localStorage.setItem("IsUserLoggedIn", "true");
              localStorage.setItem("UserData", JSON.stringify(msg.response));
              
              // Verify localStorage was set
              const storedLogin = localStorage.getItem("IsUserLoggedIn");
              const storedData = localStorage.getItem("UserData");
              console.log("[SHAREPOINT-DIALOG] ✅ localStorage verification:");
              console.log("[SHAREPOINT-DIALOG] - IsUserLoggedIn:", storedLogin);
              console.log("[SHAREPOINT-DIALOG] - UserData exists:", !!storedData);
              console.log("[SHAREPOINT-DIALOG] - UserData length:", storedData ? storedData.length : 0);
              
              if (onSuccess) {
                console.log("[SHAREPOINT-DIALOG] 🎯 Calling success callback with user data");
                onSuccess(msg.response);
              }
              
              console.log("[SHAREPOINT-DIALOG] 🚪 Closing dialog");
              dialog.close();
            }
          } catch (err) {
            console.error("[SHAREPOINT-DIALOG] ❌ Message handler error:", err);
            console.error("[SHAREPOINT-DIALOG] ❌ Error stack:", err.stack);
            if (onError) onError(err.message);
            dialog.close();
          }
        }
      );

      // Handle dialog close or error
      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (arg) => {
          let errorDescription = "";

          switch (arg.error) {
            case 12002:
              errorDescription = "Cannot load URL, no such page or bad URL syntax.";
              break;
            case 12003:
              errorDescription = "HTTPS is required.";
              break;
            case 12006:
              errorDescription = "Dialog closed by user.";
              break;
            default:
              errorDescription = "Undefined error in dialog window.";
              break;
          }

          console.log("[SHAREPOINT-DIALOG] Dialog event:", errorDescription);
          // Only report as error if it's not a user-initiated close
          if (arg.error !== 12006) {
            if (onError) onError(errorDescription);
          }
        }
      );
    }
  );
};

/**
 * Show SharePoint authentication dialog
 * Uses double-jump technique for Office Online environments
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const showSharePointAuthentication = (onSuccess, onError) => {
  console.log("[SHAREPOINT-AUTH] Starting SharePoint authentication dialog...");
  
  // Construct authentication URL
  const authUrl = `${window.location.origin}/userAuthentication.html`;
  console.log("[SHAREPOINT-AUTH] Authentication URL:", authUrl);
  
  showSharePointAuthDialog(
    authUrl,
    "signin",
    (userData) => {
      console.log("[SHAREPOINT-AUTH] ✅ SharePoint authentication successful:", userData);
      localStorage.setItem("IsUserLoggedIn", "true");
      localStorage.setItem("UserData", JSON.stringify(userData));
      console.log("[SHAREPOINT-AUTH] 💾 User data stored in localStorage");
      authenticationState = {
        isAuthenticated: true,
        user: userData,
        lastCheck: new Date()
      };
      if (onSuccess) onSuccess(userData);
    },
    (error) => {
      console.error("[SHAREPOINT-AUTH] ❌ SharePoint authentication failed:", error);
      localStorage.setItem("IsUserLoggedIn", "false");
      authenticationState = {
        isAuthenticated: false,
        user: null,
        lastCheck: new Date()
      };
      if (onError) onError(error);
    }
  );
};

/**
 * Sign out from SharePoint
 * @param {function} onSuccess - Success callback  
 * @param {function} onError - Error callback
 */
export const signOutSharePoint = (onSuccess, onError) => {
  console.log("[SHAREPOINT-AUTH] Starting SharePoint sign out...");
  
  const signoutUrl = `${window.location.origin}/userAuthentication.html?action=signout`;
  
  showSharePointAuthDialog(
    signoutUrl,
    "signout", 
    () => {
      console.log("[SHAREPOINT-AUTH] ✅ SharePoint sign out successful");
      localStorage.setItem("IsUserLoggedIn", "false");
      localStorage.removeItem("UserData");
      console.log("[SHAREPOINT-AUTH] 🗑️ User data removed from localStorage");
      authenticationState = {
        isAuthenticated: false,
        user: null,
        lastCheck: new Date()
      };
      if (onSuccess) onSuccess();
    },
    (error) => {
      console.error("[SHAREPOINT-AUTH] ❌ SharePoint sign out failed:", error);
      if (onError) onError(error);
    }
  );
};

export default {
  initializeSharePoint,
  isSharePointReady,
  handleSharePointAuth,
  showSharePointAuthentication,
  showSharePointAuthDialog,
  signOutSharePoint
};